import React, { useEffect, useState } from 'react'
import { Row, Col, Button, Spinner } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { images } from "../../../assets/images"
import { getEventDetailedRevenueReport } from "../../../api/index"
import { useNightMode } from "../../../contexts/NightModeContext"
import "./payout-receipt.scss"

const PayoutReceiptPage: React.FC = () => {
  const navigate = useNavigate()
  const { isNightMode } = useNightMode()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    const loadData = async () => {
      try {
        const eventData = localStorage.getItem("eventData")
        if (!eventData) return

        const eventInfo = JSON.parse(eventData)
        const response = await getEventDetailedRevenueReport({ eventId: eventInfo.eventId, search: "eventId" })
        setData(response.data?.[0] || null)
      } catch (err) {
        console.error("Failed to load payout data", err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const handlePrint = () => {
    const originalTitle = document.title
    const eventName = data?.title || "Event"
    const cleanEventName = eventName.replace(/[^\w\s-]/gi, '').trim().slice(0, 40)
    const eventYear = data?.eventDate ? moment(data.eventDate).format("YYYY") : moment().format("YYYY")
    const eventId = data?.eventId || "00"
    document.title = `EVENTOX-PAYOUT-${eventYear}-${eventId}-${cleanEventName}`

    const htmlElement = document.documentElement
    const bodyElement = document.body
    const isHtmlNight = htmlElement.classList.contains('night-mode')
    const isBodyNight = bodyElement.classList.contains('night-mode')
    const isHtmlDark = htmlElement.classList.contains('dark-mode')
    const isBodyDark = bodyElement.classList.contains('dark-mode')

    if (isHtmlNight) htmlElement.classList.remove('night-mode')
    if (isBodyNight) bodyElement.classList.remove('night-mode')
    if (isHtmlDark) htmlElement.classList.remove('dark-mode')
    if (isBodyDark) bodyElement.classList.remove('dark-mode')

    // Helps print CSS target this page and avoid a blank 2nd page
    bodyElement.classList.add('printing-payout-receipt')

    const cleanup = () => {
      document.title = originalTitle
      bodyElement.classList.remove('printing-payout-receipt')
      if (isHtmlNight) htmlElement.classList.add('night-mode')
      if (isBodyNight) bodyElement.classList.add('night-mode')
      if (isHtmlDark) htmlElement.classList.add('dark-mode')
      if (isBodyDark) bodyElement.classList.add('dark-mode')
      window.removeEventListener('afterprint', cleanup)
    }

    window.addEventListener('afterprint', cleanup)
    window.print()
    // Fallback if afterprint never fires (some browsers)
    setTimeout(cleanup, 1500)
  }

  const handleExportPDF = async () => {
    const receiptElement = document.querySelector('.receipt-container') as HTMLElement
    if (!receiptElement) return

    try {
      setIsExporting(true)

      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      // Small margin so edges/footer aren't clipped by PDF viewers
      const margin = 4
      const maxWidth = pageWidth - margin * 2
      const maxHeight = pageHeight - margin * 2

      // Fit the whole receipt on ONE A4 page (scale down if needed).
      // The old multi-page loop created a blank 2nd page from tiny pixel overflow.
      let imgWidth = maxWidth
      let imgHeight = (canvas.height * maxWidth) / canvas.width
      if (imgHeight > maxHeight) {
        const scale = maxHeight / imgHeight
        imgHeight = maxHeight
        imgWidth = maxWidth * scale
      }

      const x = (pageWidth - imgWidth) / 2
      const y = margin
      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)

      const eventName = data?.title || "Event"
      const cleanEventName = eventName.replace(/[^\w\s-]/gi, '').trim().slice(0, 40)
      const eventYear = data?.eventDate ? moment(data.eventDate).format("YYYY") : moment().format("YYYY")
      const eventId = data?.eventId || "00"

      pdf.save(`EVENTOX-PAYOUT-${eventYear}-${eventId}-${cleanEventName}.pdf`)
    } catch (error) {
      console.error("Failed to export PDF:", error)
    } finally {
      setIsExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <Spinner color="primary" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="page-content d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <p className="text-white-50">No payout data available.</p>
        <Button color="light" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    )
  }

  const totalFees = (data.sumStripeRevenue || 0) + (data.sumEventoxRevenue || 0)
  const currencyCode = data.organizerCountryDetails?.currency || "NZD"

  const getCurrencySymbol = (code: string) => {
    const symbols: { [key: string]: string } = {
      NZD: '$',
      AUD: '$',
      USD: '$',
      CAD: '$',
      GBP: '£',
      EUR: '€',
      INR: '₹',
      SGD: '$',
      HKD: '$',
      JPY: '¥',
      CNY: '¥',
      KRW: '₩',
      AED: 'د.إ',
      ZAR: 'R',
      PHP: '₱',
      MYR: 'RM',
      IDR: 'Rp',
      THB: '฿',
      VND: '₫',
      BRL: 'R$',
      MXN: '$'
    }
    return symbols[code.toUpperCase()] || '$'
  }

  const formatCurrency = (amount: number) =>
    `${getCurrencySymbol(currencyCode)}${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const eventInfo = JSON.parse(localStorage.getItem("eventData") || "{}")

  return (
    <div className={`page-content payout-receipt-page ${isNightMode ? 'dark-mode' : ''}`}>
      <div style={{ maxWidth: '850px', margin: '0 auto', fontFamily: "var(--font-sans)" }}>

        {/* Back + Actions bar */}
        <div className="d-flex align-items-center justify-content-between mb-4 d-print-none">
          <h4 className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: '18px', fontWeight: '700', color: isNightMode ? '#f8fafc' : '#111827' }}>
            <i className="fa-solid fa-file-invoice-dollar" style={{ color: '#F27D26' }}></i> Payout Receipt
          </h4>
          <div className="d-flex gap-2">
            <Button color="light" size="sm" onClick={handlePrint}
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 10px', color: '#64748b' }}
              className="d-flex align-items-center justify-content-center">
              <i className="fa-solid fa-print" style={{ fontSize: '1rem' }}></i>
            </Button>
            <Button size="sm" onClick={handleExportPDF} disabled={isExporting}
              style={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', color: '#fff' }}
              className="d-inline-flex align-items-center gap-1">
              {isExporting ? (
                <>
                  <Spinner size="sm" style={{ width: '0.8rem', height: '0.8rem' }} /> EXPORTING...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download" style={{ fontSize: '0.9rem' }}></i> EXPORT .PDF
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="receipt-container">
          <div className="receipt-content">

            {/* ── Header ── */}
            <div className="p-4 p-sm-5 p-md-8 border-bottom relative overflow-hidden" style={{ borderColor: '#f1f5f9' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: '250px', height: '250px', backgroundColor: 'rgba(242, 125, 38, 0.05)', borderRadius: '50%', marginLeft: '-125px', marginTop: '-125px', filter: 'blur(40px)', opacity: 0.5, pointerEvents: 'none' }} />
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 position-relative" style={{ zIndex: 10 }}>
                <div>
                  <h1 style={{ fontSize: '1rem', fontWeight: '800', letterSpacing: '0.5px', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    PAYOUT <span style={{ color: '#E91E63' }}>RECEIPT</span>
                  </h1>
                  {/* <p className="mb-0 mt-1" style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '500' }}>Ref No: EVT-PAYOUT-{moment(data.eventDate || undefined).format("YYYY")}-{data.eventId || "00"}</p>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      backgroundColor: '#dcfce7', color: '#16a34a',
                      fontSize: '8px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase'
                    }}>
                      <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }}></span>
                      {data.eventStatus || "Generated"}
                    </span>
                  </div> */}
                </div>
                <div className="text-sm-end d-flex flex-column align-items-sm-end mt-2 mt-sm-0">
                  <img src={images.eventox_logo} alt="EVENTOX LOGO" style={{ height: '35px', objectFit: 'contain' }} className="mb-1 logo-dark" />
                  <img src={images.eventox_blackLogo} alt="EVENTOX LOGO" style={{ height: '24px', objectFit: 'contain' }} className="mb-1 logo-light" />
                  <p className="mb-0" style={{ fontSize: '8px', letterSpacing: '0.3em', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase' }}>EXPERIENCE</p>
                </div>
              </div>
            </div>

            {/* ── Net Settlement Banner ── */}
            <div className="px-4 px-sm-5 px-md-8 py-4 border-bottom d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-4 bg-white" style={{ borderColor: '#f8fafc' }}>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#F27D26', boxShadow: '0 0 8px rgba(242,125,38,0.4)' }} />
                  <p className="mb-0" style={{ fontSize: '9px', fontWeight: '900', color: '#94a3b8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Net Settlement Amount</p>
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <h2 className={`mb-0 net-settlement-amount ${data.sumOrganizerRevenue < 0 ? 'negative' : ''}`} style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: '2.5rem', fontWeight: '900', color: data.sumOrganizerRevenue < 0 ? '#E91E63' : '#111827',
                    letterSpacing: '-2px', lineHeight: '1'
                  }}>
                    {formatCurrency(data.sumOrganizerRevenue || 0)}
                  </h2>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: '10px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px' }}>{currencyCode}</span>
                </div>
              </div>
              <div className="d-flex gap-4 gap-sm-5 text-start text-sm-end">
                <div>
                  <p style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Gross Sales</p>
                  <p className="mb-0 gross-sales-amount" style={{ fontFamily: "var(--font-mono)", fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>{formatCurrency(data.sumGrandTotal || 0)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Total Deductions</p>
                  <p className="mb-0 deductions-amount" style={{ fontFamily: "var(--font-mono)", fontSize: '1.25rem', fontWeight: '700', color: '#E91E63' }}>-{formatCurrency(totalFees)}</p>
                </div>
              </div>
            </div>

            {/* ── Info Content Grid ── */}
            <div className="p-4 p-sm-5 p-md-8" style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

              {/* ── Section 1: Analytics & Identity ── */}
              <Row className="g-4 analytics-identity-row">
                <Col xs={12} md={6}>
                  <div className="d-flex align-items-center gap-2 pb-2 mb-3 section-header-divider" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <i className="fa-solid fa-file-invoice" style={{ color: '#F27D26', fontSize: '1rem' }}></i>
                    <h3 className="mb-0 section-header-title" style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', color: '#111827', textTransform: 'uppercase' }}>Receipt Analytics</h3>
                  </div>
                  <Row className="g-3">
                    <Col xs={6}>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Date Issued</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{moment().format("DD MMMM YYYY")}</p>
                    </Col>
                    <Col xs={6}>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Requested On</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{moment().format("DD MMMM YYYY")}</p>
                    </Col>
                    <Col xs={6}>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Payout Initiated</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Pending</p>
                    </Col>
                    <Col xs={6}>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Currency</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{currencyCode} ({getCurrencySymbol(currencyCode)})</p>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md={6} className="ps-md-4">
                  <div className="d-flex align-items-center gap-2 pb-2 mb-3 section-header-divider" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <i className="fa-solid fa-user" style={{ color: '#F27D26', fontSize: '1rem' }}></i>
                    <h3 className="mb-0 section-header-title" style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', color: '#111827', textTransform: 'uppercase' }}>Organizer Identity</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Organizer Name</label>
                      <p className="mb-0 grid-value font-weight-bold" style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{data.organizerDetails?.name || data.organizerDetails?.firstName || "N/A"}</p>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Event Property</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#4b5563', lineHeight: '1.3' }}>{data.title || "N/A"}</p>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Venue Destination</label>
                      <p className="mb-0 grid-value" style={{ fontSize: '13px', fontWeight: '500', color: '#4b5563' }}>{eventInfo.eventLocation || eventInfo.venue || "N/A"}</p>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* ── Section 2: Payout Breakdown (Table) ── */}
              <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="fa-solid fa-right-left" style={{ color: '#F27D26', fontSize: '1rem' }}></i>
                  <h3 className="mb-0" style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', color: '#111827', textTransform: 'uppercase' }}>Payout Breakdown</h3>
                </div>

                <div className="table-container" style={{ overflow: 'hidden', border: '1px solid #f3f4f6', borderRadius: '1rem' }}>
                  <table className="w-100 border-collapse" style={{ margin: 0 }}>
                    <thead>
                      <tr className="table-header-row" style={{ backgroundColor: 'rgba(249, 250, 251, 0.5)' }}>
                        <th style={{ padding: '12px 24px', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', border: 'none' }}>Description</th>
                        <th className="text-end" style={{ padding: '12px 24px', fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', border: 'none' }}>Amount ({currencyCode})</th>
                      </tr>
                    </thead>
                    <tbody style={{ borderTop: 'none' }}>
                      {data.sumTotalTicketAmount > 0 && (
                        <tr className="table-body-row" style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="desc-cell" style={{ padding: '14px 24px', fontSize: '13px', fontWeight: '500', color: '#4b5563', border: 'none' }}>
                            Gross Collected Revenue
                          </td>
                          <td className="text-end amount-cell" style={{
                            padding: '14px 24px',
                            fontFamily: "var(--font-mono)",
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#111827',
                            border: 'none'
                          }}>
                            {formatCurrency(data.sumGrandTotal)}
                          </td>
                        </tr>
                      )}

                      {totalFees > 0 && (
                        <tr className="table-body-row" style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td className="desc-cell" style={{ padding: '14px 24px', fontSize: '13px', fontWeight: '500', color: '#4b5563', border: 'none' }}>
                            Fees
                          </td>
                          <td className="text-end amount-cell negative" style={{
                            padding: '14px 24px',
                            fontFamily: "var(--font-mono)",
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#E91E63',
                            border: 'none'
                          }}>
                            -{formatCurrency(totalFees)}
                          </td>
                        </tr>
                      )}

                      <tr className="total-settlement-row" style={{ backgroundColor: '#000000', color: '#fff' }}>
                        <td style={{ padding: '40px 24px', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', border: 'none' }}>
                          Total Settlement Balance
                        </td>
                        <td className="text-end amount-cell total" style={{
                          padding: '24px 24px',
                          fontFamily: "var(--font-mono)",
                          fontSize: '1.75rem',
                          fontWeight: '900',
                          letterSpacing: '-1px',
                          color: '#fff',
                          border: 'none'
                        }}>
                          {formatCurrency(data.sumOrganizerRevenue || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div className="receipt-footer" style={{
              backgroundColor: '#0f172a',
              color: '#fff',
              borderBottomLeftRadius: '1.5rem',
              borderBottomRightRadius: '1.5rem'
            }}>
              <Row className="g-4 footer-row">
                <Col xs={12} md={7}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="fa-solid fa-circle-info" style={{ color: '#F27D26', fontSize: '1rem' }}></i>
                    <h3 className="mb-0 text-uppercase" style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', color: '#94a3b8' }}>Important Disclosures</h3>
                  </div>
                  <ul className="list-unstyled mb-0" style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 0 }}>
                    <li className="d-flex align-items-start gap-2" style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                      <i className="fa-solid fa-chevron-right" style={{ color: '#F27D26', fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}></i>
                      <span>Funds include cleared transactions up to payout initiation.</span>
                    </li>
                    <li className="d-flex align-items-start gap-2" style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                      <i className="fa-solid fa-chevron-right" style={{ color: '#F27D26', fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}></i>
                      <span>Pending transactions roll forward to the subsequent cycle.</span>
                    </li>
                    <li className="d-flex align-items-start gap-2" style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
                      <i className="fa-solid fa-chevron-right" style={{ color: '#F27D26', fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}></i>
                      <span>System-generated authentic document; no signature required.</span>
                    </li>
                  </ul>
                </Col>
                <Col xs={12} md={5} className="text-start text-md-end d-flex flex-column align-items-start align-items-md-end justify-content-between gap-4">
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#fff', marginBottom: '2px' }}>
                      Vibra Verse Ltd <span style={{ color: '#F27D26' }}>NZ</span>
                    </h3>
                    <p className="mb-0" style={{ fontSize: '11px', color: '#64748b', fontFamily: "var(--font-mono)" }}>info@eventox.co</p>
                    <p className="mb-0" style={{ fontSize: '11px', color: '#64748b', fontFamily: "var(--font-mono)" }}>www.eventox.co</p>
                  </div>
                  <div style={{
                    display: 'inline-block', border: '1px solid #334155',
                    borderRadius: '999px', padding: '4px 14px',
                    fontSize: '9px', color: '#64748b',
                    letterSpacing: '2.5px', fontWeight: '700', textTransform: 'uppercase'
                  }}>
                    SECURE • TRANSPARENT • RELIABLE
                  </div>
                </Col>
              </Row>

              <hr style={{ borderColor: 'rgba(255,255,255,0.08)', margin: '20px 0' }} />

              <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '9px', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: '700', color: '#475569' }}>
                <span>Generated by Eventox Intelligence</span>
                <span>© {new Date().getFullYear()} EVENTOX PAYOUT SYSTEM</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

export default PayoutReceiptPage
